import React, { useState } from 'react';
import { toast } from 'sonner';
import { psychologistsApi } from '@/api/psychologists';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BRAZILIAN_STATES } from '@/constants';
import { useAuth, useSyncAuthUser } from '@/context/AuthContext';
import { masks } from '@/utils/masks';

interface MissingCrpModalProps {
    close: () => void;
    onSuccess: () => Promise<void> | void;
}

export const MissingCrpModal: React.FC<MissingCrpModalProps> = ({ close, onSuccess }) => {
    const { user } = useAuth();
    const syncAuthUser = useSyncAuthUser();
    const [crp, setCrp] = useState('');
    const [state, setState] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user?.id) return;
        if (!crp || !state) {
            toast.error('Preencha o CRP e o estado');
            return;
        }

        const crpPattern = /^(?:CRP\s*)?\d{2}\/\d{4,6}$/;
        if (!crpPattern.test(crp)) {
            toast.error('Formato de CRP inválido. Use o formato 00/00000');
            return;
        }

        try {
            setIsLoading(true);
            const updated = await psychologistsApi.update(user.id, {
                crp: crp,
                crp_state: state,
            });
            // The export gate and the laudo's cabeçalho both read the CRP off
            // the auth cache, so the save only counts once that is refreshed.
            await syncAuthUser(updated);
            toast.success('CRP atualizado com sucesso');
            await onSuccess();
        } catch (error) {
            console.error('Error updating CRP', error);
            toast.error('Erro ao atualizar CRP');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                    Para exportar o laudo, é necessário que você tenha o seu número de CRP
                    cadastrado. Por favor, preencha abaixo para continuar.
                </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                        <Label htmlFor="crp">Número do CRP</Label>
                        <Input
                            id="crp"
                            value={crp}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setCrp(masks.crp(event.target.value))
                            }
                            placeholder="00/00000"
                        />
                    </div>
                    <div className="col-span-1 space-y-2">
                        <Label htmlFor="state">Estado</Label>
                        <Select value={state} onValueChange={setState}>
                            <SelectTrigger id="state">
                                <SelectValue placeholder="UF" />
                            </SelectTrigger>
                            <SelectContent>
                                {BRAZILIAN_STATES.map((uf) => (
                                    <SelectItem key={uf} value={uf}>
                                        {uf}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={close}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? 'Salvando...' : 'Salvar e Continuar'}
                    </Button>
                </div>
            </form>
        </div>
    );
};
